import { Form, Select, Alert, App } from 'antd';
import { trpc } from '../../../../lib/api';
import { useState } from 'react';
import { withDevModalFilePath } from '../../../../components/common';

interface AssignCouponToAgencyModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const { Modal } = withDevModalFilePath('client/src/pages/admin/subscriptions/components/AssignCouponToAgencyModal.tsx');

export function AssignCouponToAgencyModal({
  open,
  onCancel,
  onSuccess,
}: AssignCouponToAgencyModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isAssigningAll, setIsAssigningAll] = useState(false);
  const selectedAgency = Form.useWatch('agencyUserId', form);

  // 代理店一覧取得
  const { data: agenciesData, isLoading: agenciesLoading, error: agenciesError } = trpc.agencyAdmin.listAgencyUsers.useQuery({
    page: 1,
    // API側の最大値（schema max: 100）を超えないようにする
    limit: 100,
  });

  // Stripe クーポン一覧取得
  const { data: couponsData, isLoading: couponsLoading, error: couponsError } = trpc.stripeCouponAdmin.listCoupons.useQuery({
    limit: 100,
  });


  // 割り当てミューテーション
  const assignMutation = trpc.agencyCouponAssignmentAdmin.assign.useMutation({
    onSuccess: () => {
      message.success('クーポンを代理店に割り当てました');
      form.resetFields();
      onSuccess();
    },
    onError: (error) => {
      message.error(error.message || 'クーポンの割り当てに失敗しました');
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // 「全て」が選択された場合
      if (values.agencyUserId === '__ALL__') {
        const agencies = agenciesData?.users ?? [];
        if (agencies.length === 0) {
          message.error('割り当て可能な代理店が見つかりません');
          return;
        }

        setIsAssigningAll(true);
        let successCount = 0;
        let skippedCount = 0;
        let errorCount = 0;

        // 全代理店に順次割り当て
        for (const agency of agencies) {
          try {
            await assignMutation.mutateAsync({
              agencyUserId: agency.id,
              stripeCouponId: values.stripeCouponId,
            });
            successCount++;
          } catch (error: unknown) {
            // 既に同じクーポンが割り当てられている場合
            const errorMessage = error instanceof Error ? error.message : '';
            if (errorMessage.includes('既に同じクーポンが割り当てられています')) {
              skippedCount++;
            } else {
              errorCount++;
            }
          }
        }

        setIsAssigningAll(false);

        if (errorCount === 0 && skippedCount === 0) {
          message.success(`${successCount}件の代理店にクーポンを割り当てました`);
        } else if (errorCount === 0) {
          message.success(`${successCount}件割り当て、${skippedCount}件スキップしました（既に割り当て済み）`);
        } else {
          message.warning(`${successCount}件成功、${skippedCount}件スキップ、${errorCount}件失敗しました`);
        }

        form.resetFields();
        onSuccess();
      } else {
        // 単一の代理店に割り当て
        assignMutation.mutate(values);
      }
    } catch {
      // バリデーションエラー
    }
  };

  return (
    <Modal
      title="クーポンを代理店に割り当て"
      open={open}
      onCancel={() => {
        form.resetFields();
        setIsAssigningAll(false);
        onCancel();
      }}
      onOk={handleSubmit}
      confirmLoading={assignMutation.isPending || isAssigningAll}
      okButtonProps={{ disabled: isAssigningAll }}
      okText="割り当て"
      cancelText="キャンセル"
      width={600}
    >
      <Form form={form} layout="vertical">
        {agenciesError && (
          <Alert
            message="代理店データの取得に失敗しました"
            description={agenciesError.message}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {couponsError && (
          <Alert
            message="クーポンデータの取得に失敗しました"
            description={couponsError.message}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="agencyUserId"
          label="代理店"
          rules={[{ required: true, message: '代理店を選択してください' }]}
        >
          <Select
            placeholder="代理店を選択"
            showSearch
            optionFilterProp="label"
            loading={agenciesLoading}
            disabled={agenciesLoading}
            notFoundContent={agenciesLoading ? 'データ読み込み中...' : '代理店が見つかりません'}
            options={[
              {
                value: '__ALL__',
                label: `🌐 全ての代理店に割り当て (${agenciesData?.users?.length || 0}件)`,
              },
              ...((agenciesData?.users ?? []).map((agency) => ({
                value: agency.id,
                label: `${agency.name} (${agency.couponPrefix})`,
              }))),
            ]}
          />
        </Form.Item>

        {selectedAgency === '__ALL__' && (
          <Alert
            message="一括割り当て"
            description={`全ての代理店（${agenciesData?.users?.length || 0}件）にこのクーポンを割り当てます。既に同じクーポンが割り当てられている代理店はスキップされます。`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="stripeCouponId"
          label="Stripe クーポン"
          rules={[{ required: true, message: 'クーポンを選択してください' }]}
          extra="選択したクーポンから、代理店が1回だけプロモーションコードを発行できます（99999回使用可能）"
        >
          <Select
            placeholder="クーポンを選択"
            showSearch
            optionFilterProp="label"
            loading={couponsLoading}
            disabled={couponsLoading}
            notFoundContent={couponsLoading ? 'データ読み込み中...' : 'クーポンが見つかりません'}
            options={(couponsData?.data ?? [])
              .filter((c) => c.valid)
              .map((coupon) => ({
                value: coupon.id,
                label: `${coupon.name || coupon.id} - ${
                  coupon.percent_off
                    ? `${coupon.percent_off}% OFF`
                    : `¥${(coupon.amount_off || 0) / 100} OFF`
                } (${
                  coupon.duration === 'once'
                    ? '1回のみ'
                    : coupon.duration === 'forever'
                    ? '永続'
                    : `${coupon.duration_in_months}ヶ月`
                })`,
              }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}



